/**
 * Task (Pendenz) wire types — shared between apps/web and apps/api.
 * Single source of truth for the split-view API + action contracts.
 *
 * snake_case field names match the DB as-built (api-spec conventions).
 * Mirror the as-built column names (comm_id, assigned_to, body, from, to, cc,
 * content, meta) — do NOT use doc-only names.
 *
 * [Source: architecture/api-spec.md#6-tasks-pendenz]
 * [Source: architecture/coding-standards.md#1-cross-cutting-conventions-all-languages]
 * [Source: docs/stories/2.4-split-view-bulk.md#task-1]
 */

import type { ChannelKey } from './channel';

// ── Status / routing / process unions ────────────────────────────────────────

/** task.status values — mirrors task_status Postgres enum (as-built). */
export type TaskStatus = 'open' | 'in_progress' | 'awaiting_approval' | 'done' | 'cancelled';

/** task.routing values — mirrors task_routing Postgres enum (as-built). */
export type TaskRouting = 'automated' | 'freigabe' | 'manual';

/** task.process values — mirrors task_process Postgres enum (as-built). */
export type TaskProcess =
  | 'email_intake'
  | 'org_genesis'
  | 'zefix_enrich'
  | 'mandate_signing'
  | 'status_change'
  | 'doc_classification'
  | 'invoice_check'
  | 'invoice_manual_match'       // Story 10.2 — "Rechnung manuell zuordnen" no-hit Task
  | 'policy_endorsement_review'  // Story 10.3 — "record Nachtrag" Task (unrecorded-endorsement Auslöser)
  | 'commission_manual_match'    // Story 11.4 — "Courtagezeile manuell zuordnen" no-hit Task
  | 'other'
  // Story V10-F: additive type-widening — the union was stale vs the DB enum
  // (verified: no exhaustive switch breaks; the DB enum is the source of truth).
  | 'invoice_k2_review'          // Story 10.6 fix — K2 "Differenz prüfen" review Task
  | 'invoice_k3_escalation'      // Story 10.6 fix — K3 Mandatsleiter escalation Task
  | 'renewal_due'                // Story R2 — renewal alert Task
  | 'portal_message'             // Story V10-D — portal message intake Task
  | 'portal_mutation'            // Story V10-D — portal profile-change intake Task
  | 'document_upload'            // §4.7 — the triage Aufgabe of an Ablage upload's Eingang
  | 'carrier_notification_address' // P4-P6 Foundation Slice B / F1 (0170) — «Benachrichtigungsadresse pflegen»
  | 'mandate_ruecklauf'          // P4-S3 (0174): «Rücklauf zuordnen» / «Rücklauf verspätet» (the review tasks use doc_classification)
  | 'insurer_notification'       // P4-S8 (0178): «Versicherer benachrichtigen» (a Nachmeldung late relationship)
  | 'mandate_registration'       // 0188: the Brokermandat registration (Meldungen freigeben, Meldeliste prüfen, ...)
  | 'mandate_termination'        // 0193: P6 «Mandatsauflösung freigeben» (an eröffnete termination waits for its Freigabe)
  | 'customer_retention';        // 0198: P6-S7 «Löschvorschlag» (the retention of an inaktiv customer ran out)

/**
 * ALL as-built task_process enum values, in DB-enum (append) order.
 *
 * Story V10-F: the group-routing admin PUT validates `process` against THIS
 * full list — NOT the 8-value ROUTABLE_PROCESSES constant (that one belongs to
 * confidence routing only; group routing may target ANY process — review and
 * escalation task types are exactly what teams pull).
 *
 * APPEND-ONLY: mirrors packages/db/src/schema/_enums.ts taskProcess + the
 * ALTER TYPE … ADD VALUE migrations (0024, 0025, 0027, 0031, 0038, 0053).
 */
export const TASK_PROCESS_VALUES = [
  'email_intake',
  'org_genesis',
  'zefix_enrich',
  'mandate_signing',
  'status_change',
  'doc_classification',
  'invoice_check',
  'other',
  'invoice_manual_match',       // 0024
  'policy_endorsement_review',  // 0025
  'invoice_k2_review',          // 0027
  'invoice_k3_escalation',      // 0027
  'commission_manual_match',    // 0031
  'renewal_due',                // 0038
  'portal_message',             // 0053
  'portal_mutation',            // 0053
  'carrier_notification_address', // 0170 (P4-P6 Foundation Slice B / F1)
  'mandate_ruecklauf',            // 0174 (P4-S3)
  'insurer_notification',         // 0178 (P4-S8)
  'mandate_registration',         // 0188
  'mandate_termination',          // 0193
  'customer_retention',           // 0198 (P6-S7)
] as const satisfies readonly TaskProcess[];

// ── Action constants ──────────────────────────────────────────────────────────

/**
 * M1 action set for Task processing endpoints.
 * Status-only (D-SPLIT-ACTIONS: approve/reject + pending_action execution deferred to Epic 3+).
 * No magic string literals at call sites — always use TASK_ACTIONS.<key>.
 *
 * [Source: architecture/coding-standards.md#1-cross-cutting-conventions-all-languages]
 * [Source: docs/stories/2.4-split-view-bulk.md#d-split-actions]
 */
export const TASK_ACTIONS = {
  COMPLETE: 'complete',
  CANCEL: 'cancel',
  REOPEN: 'reopen',
  IN_PROGRESS: 'in_progress',
  ASSIGN: 'assign',
  // Story V10-F — Funktionsgruppen (task-routing teams):
  // ASSIGN_GROUP sets/clears task.assigned_group_id (assigned_to untouched);
  // CLAIM assigns an unassigned group-queue task to the CALLER (resolved
  // app_user.id — never from the body). Both are dedicated endpoints
  // (POST /tasks/:id/assign-group, POST /tasks/:id/claim), not applyAction arms.
  ASSIGN_GROUP: 'assign_group',
  CLAIM: 'claim',
} as const;

export type TaskActionCode = (typeof TASK_ACTIONS)[keyof typeof TASK_ACTIONS];

// ── List-row shape ────────────────────────────────────────────────────────────

/**
 * TaskSummary — the worklist row shape (GET /tasks list).
 * Joined to communication to surface subject/from/received_at.
 * needs_review: derived from task.meta->>'needs_review'.
 * tenant_id is NOT included (implicit from token; never returned to clients).
 */
export interface TaskSummary {
  id: string;
  process: TaskProcess;
  status: TaskStatus;
  routing: TaskRouting;
  title: string | null;
  comm_id: string | null;
  assigned_to: string | null;
  /** Story V10-F: the optional Funktionsgruppe assignment (alongside assigned_to). */
  assigned_group_id: string | null;
  due_at: string | null;
  completed_at: string | null;
  needs_review: boolean;
  /**
   * Story V10-J2: the task subject (Kunde) — polymorphic person/firma link.
   * Was always emitted by TaskDto; now part of the wire contract so the web
   * can link Eingang rows to the Kunde detail.
   */
  subject_type: string | null;
  subject_id: string | null;
  /**
   * Story V10-J2: the routing confidence — numeric(4,3) carried as a string
   * ("0.920"); null when no confidence was scored. Was always selected by the
   * list SQL + emitted by TaskDto — now part of the wire contract so the web
   * can render the real Konfidenz instead of a routing-derived display value.
   */
  confidence: string | null;
  /**
   * Story V10-J2: subject display name joined from task.subject_type/subject_id
   * (person: "last_name first_name" · firma: name — the GET /customers
   * name-resolution SQL). Null for tasks without a subject (or RLS-hidden).
   * Optional: not every list SQL (e.g. group queue) joins it.
   */
  kunde_name?: string | null;
  /**
   * Issue 20: the assignee display name resolved server-side from
   * task.assigned_to (app_user.display_name, scoped to the tenant's members).
   * Null when the task is unassigned, when the holder is no longer a member, or
   * when the user has no display name. Optional: not every list SQL joins it.
   */
  assignee_name?: string | null;
  /**
   * Issue 20: the Funktionsgruppe display name resolved server-side from
   * task.assigned_group_id (funktionsgruppe.name). Null when the task carries no
   * explicit group. Optional: not every list SQL joins it.
   */
  assigned_group_name?: string | null;
  // Joined from communication (null for manual tasks):
  subject?: string | null;
  from?: string | null;
  received_at?: string | null;
  created_at: string;
  updated_at: string;
  /**
   * The staged-approval marker (task.meta.approval), when present.
   * Status stays 'awaiting_approval' while staged (a new status enum value needs
   * a migration); this is how a caller sees that a single approval was already given.
   */
  approval?: TaskApproval | null;
  /**
   * The Vorgang whose Prüfbericht a mandate review task decides (task.meta.vorgang_id of a task
   * opened for a control run); null for every other task. The task detail mounts the report with it.
   */
  pruefbericht_vorgang_id?: string | null;
  /**
   * A registration task's kind (task.meta.kind of process mandate_registration: eintragungsbestaetigung_pruefen,
   * widerspruechliche_antwort, eintragungsbestaetigung_zuordnen, ...); null for every other task.
   */
  registration_kind?: string | null;
  /**
   * P6-S7: the Löschvorschlag's own data (task.meta of process customer_retention): the confirmation label
   * the approval compares and the retention date; null for every other task.
   */
  retention_proposal?: { confirmation_label: string; retention_until: string | null } | null;
}

// ── Split-view response shapes ────────────────────────────────────────────────

/**
 * CommunicationView — the left pane of the split-view.
 * Full email (un-tiered in M1 — D-SPLIT-NO-TIER).
 * No blob_url, no internal columns, no tenant_id.
 * Uses as-built column names (body not body_text; from not from_addr).
 *
 * [Source: docs/stories/2.4-split-view-bulk.md#as-built-schema]
 */
export interface CommunicationView {
  id: string;
  channel: string;
  direction: string;
  subject: string | null;
  from: string | null;
  to: string | null;
  cc: string | null;
  message_id: string | null;
  received_at: string;
  body: string | null;
  body_html: string | null;
}

/**
 * CommVisibilityTier — the effective visibility tier the caller has for a communication.
 * Mirrors the as-built `visibility_tier` Postgres enum:
 *   'full'     → subject + body + body_html (the viewer is a participant / owner / granted).
 *   'title'    → subject only; body + body_html hidden.
 *   'metadata' → nothing (participants + date come from other endpoints).
 */
export type CommVisibilityTier = 'full' | 'title' | 'metadata';

/**
 * CommunicationContentDto — GET /communications/:commId/content payload.
 *
 * The caller's effective-tier-projected body of ANY communication, independent of whether
 * a Task is linked to it (the split view only exposed the body through a linked task). Many
 * emails are HTML-only (`body` NULL, `body_html` populated), so BOTH are returned at 'full'.
 * The server projects per tier (never leaks body across tiers):
 *   full     → subject + body + body_html
 *   title    → subject only; body + body_html = null
 *   metadata → everything null
 *
 * Berechtigungskonzept V3.0 §2.2 (Bereich 1 Privat-Markierung): is_private/can_mark_private
 * only ever reach the caller when they already passed the server's hard-exclusion check —
 * a non-participant viewer gets a 404 for a private comm, never this DTO (UC9).
 */
export interface CommunicationContentDto {
  communication_id: string;
  your_effective_tier: CommVisibilityTier;
  /** Is this comm currently marked private? */
  is_private: boolean;
  /** May the CALLING viewer mark/unmark this comm private (sender OR internal to/cc)? */
  can_mark_private: boolean;
  /**
   * MV (mail-only team visibility): the NAME of the team this mail is restricted to
   * (', '-joined when the comm is linked to several restricted clients), or null when the
   * mail is unrestricted. Drives the "Vertraulich" marker in the reading pane. A viewer
   * who may NOT see the mail gets a 404 instead of this payload, so a non-null value here
   * always means "you may read it, and this is why it is marked".
   */
  restricted_to_team: string | null;
  subject: string | null;
  body: string | null;
  body_html: string | null;
  /**
   * Bereich 1 "Mail bleibt Master" (on-demand Graph fetch): true when the returned
   * body/subject were fetched LIVE from the mailbox owner's Microsoft-Graph connection
   * for THIS view (a Stufe-2/3 mail whose content was never stored) rather than read
   * from communication.body. The content is never persisted — it is rendered per view.
   */
  content_on_demand?: boolean;
  /**
   * Tier 'full' only (issue 31): stored document rows (Stufe 1) or live provider metadata
   * (Stufe 2/3, nothing persisted); reduced tiers []. null + attachments_unavailable=true
   * = the live listing FAILED, render loudly, never "Keine Anhänge" (rule #1); [] = none.
   */
  attachments: CommunicationAttachmentDto[] | null;
  attachments_unavailable: boolean;
  /** Genesis intake outcome and the facts it was decided from. Optional on the wire, always sent. */
  genesis?: { outcome: GenesisOutcome; facts: GenesisFact[] } | null;
  /**
   * The mail's representative linked customer when that customer is CURRENTLY open unconfirmed
   * (CUSTOMER_UNCONFIRMED_OPEN_SQL; the column is_unconfirmed alone is not enough), else null.
   * NEVER `genesis.outcome`, which is intake history and stays 'unconfirmed' once written.
   * Carries the subject the confirm and decline actions address.
   */
  unconfirmed: { subject_type: 'firma' | 'person'; subject_id: string; name: string | null } | null;
}

/**
 * One /content attachment. on_demand=true: bytes live only at the provider, `id` = the
 * provider attachment id for the comm attachment download route; false: `id` = document id.
 */
export interface CommunicationAttachmentDto {
  id: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  on_demand: boolean;
}

/** The genesis intake decision for a communication's sender. */
export type GenesisOutcome = 'reject' | 'unconfirmed' | 'attached' | 'internal';

/** One fact the genesis outcome was decided from, discriminated on `kind`. */
export type GenesisFact =
  | { kind: 'register'; name: string; uid: string | null; seat: string | null }
  | { kind: 'reply_to'; sent_at: string }
  | { kind: 'none' };

/**
 * AttachmentView — attachment metadata from the document table.
 * blob_url is INTENTIONALLY ABSENT (D-SPLIT-NO-DOWNLOAD: API-gated secret path).
 * Linked via document.linked_type='communication' AND linked_id=task.comm_id.
 *
 * [Source: docs/stories/2.4-split-view-bulk.md#d-split-no-download]
 */
export interface AttachmentView {
  id: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
}

/**
 * TaskApproval: the staged-approval marker stored at task.meta.approval.
 * `staged` is true once a single approval was given; task.status is left
 * UNCHANGED (still 'awaiting_approval') until the batch is fully approved.
 */
export interface TaskApproval {
  staged: boolean;
  approved_by: string | null;
  approved_at: string | null;
  held_action: string | null;
  /** True when this approval was staged by the confidence router, not a human. Defaults to false when absent. */
  auto?: boolean;
}

/**
 * TaskAttachmentView: one file's send-control state for a task, the Versandanhänge list.
 * Seeded from the comm's documents; never deleted, only (de)activated. delivery_channel is
 * restricted to VERSAND_CHANNEL_KEYS.
 */
export interface TaskAttachmentView {
  task_id: string;
  document_id: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  delivery_channel: ChannelKey;
  channel_overridden: boolean;
  is_active: boolean;
  deactivated_at: string | null;
  /** A person switched the file off, not the seed (which deactivates inline pictures). */
  deactivated_by_user: boolean;
}

/** PATCH /tasks/:id/attachments/:documentId body: at least one field required. */
export interface TaskAttachmentPatch {
  delivery_channel?: ChannelKey;
  is_active?: boolean;
}

/**
 * TaskDraftView: the Antwortentwurf (draft reply) state of one task. One row per task,
 * generated on first read from the AI assist and editable until the task closes.
 * original_ai_text never changes after generation; draft_text is the current text.
 */
export interface TaskDraftView {
  task_id: string;
  original_ai_text: string;
  draft_text: string;
  is_edited: boolean;
  edited_at: string | null;
  model: string;
  confidence_percent: number;
  generated_at: string;
}

/**
 * Upper bound of draft_text, in characters. The api DTO and the UI both count against this
 * one constant, so the limit cannot drift between them.
 */
export const TASK_DRAFT_TEXT_MAX_LENGTH = 20000;

/** PUT /tasks/:id/draft body: an empty string is valid and means "send nothing". */
export interface TaskDraftPut {
  draft_text: string;
}

/**
 * ActivityEntry — one activity timeline row.
 * actor_id mirrors as-built activity.actor_id (docs call it user_id).
 * content mirrors as-built activity.content (docs call it summary).
 * meta mirrors as-built activity.meta (docs call it detail).
 *
 * [Source: docs/stories/2.4-split-view-bulk.md#as-built-schema]
 */
export interface ActivityEntry {
  id: string;
  actor_kind: string;
  actor_id: string | null;
  type: string;
  content: string | null;
  meta: Record<string, unknown> | null;
  created_at: string;
}

/**
 * TaskDetail — the right pane of the split-view.
 * Includes all task columns + needs_review derived from task.meta.
 * tenant_id is NOT included.
 */
export interface TaskDetail {
  id: string;
  process: TaskProcess;
  status: TaskStatus;
  routing: TaskRouting;
  title: string | null;
  comm_id: string | null;
  assigned_to: string | null;
  /** Story V10-F: the optional Funktionsgruppe assignment (alongside assigned_to). */
  assigned_group_id: string | null;
  subject_type: string | null;
  subject_id: string | null;
  closes_task_id: string | null;
  due_at: string | null;
  completed_at: string | null;
  confidence: string | null;
  created_by_kind: string;
  needs_review: boolean;
  created_at: string;
  updated_at: string;
  /** Free-text review note (task.meta.review_note). Optional on the wire, always sent. */
  review_note?: string | null;
}

/**
 * SplitViewResponse — the AC1 split-view read payload.
 * task (right pane) + communication (left pane) + attachments + activity.
 * communication is null for manual tasks (no linked comm).
 *
 * [Source: architecture/api-spec.md#6-tasks-pendenz]
 */
export interface SplitViewResponse {
  task: TaskDetail;
  communication: CommunicationView | null;
  attachments: AttachmentView[];
  activity: ActivityEntry[];
  /** The server-resolved visibility tier of the caller for this communication; null for a task without one. */
  your_effective_tier?: CommVisibilityTier | null;
}

// ── Bulk result ───────────────────────────────────────────────────────────────

/**
 * TaskBulkResult — the POST /tasks/bulk response.
 * Per-id success/failure — one entry per input id.
 * Per-item failures do NOT abort the batch (D-SPLIT-BULK-TX).
 * Compliance: each affected object gets its OWN activity + audit row (AC3).
 *
 * [Source: architecture/security-compliance.md#4-immutable-audit-log]
 * [Source: architecture/api-spec.md#6-tasks-pendenz]
 */
export interface TaskBulkResult {
  results: Array<{
    id: string;
    ok: boolean;
    error?: {
      code: string;
      message: string;
    };
  }>;
}
